'use client';

import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { useTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import type { ExposureTab } from '@/lib/analytics/client/events';
import { SubSectionTitle } from '@/components/common/SubSectionTitle';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { getParsedValue } from '@/helpers/formatters';
import { trackExposureRowClicked, trackExposureTabChanged } from '@/lib/analytics/client/trackers/exposure';
import PublicAllocatorSection from '../../VaultV2Allocation/PublicAllocator/PublicAllocatorSection';
import VaultV2ExposureCategoryRow from './components/VaultV2ExposureCategoryRow';
import { getColumnsVaultV2Exposure, getVaultV2ExposureColumnLabels, vaultV2ExposureTableFeatures } from './config/VaultV2Exposure.columns';
import { EXPOSURE_TABS, type ExposureTabKey } from './config/VaultV2Exposure.tabs';
import { useVaultV2ExposureData, type VaultV2ExposureSectionData, type VaultV2ExposureTab, type VaultV2ExposureTableRowItem } from './hooks/useVaultV2ExposureData';
function getExposureRowDestination(item: VaultV2ExposureTableRowItem['item']): {
  type: 'market' | 'vault';
  identifier: string;
} | undefined {
  switch (item.type) {
    case 'MarketV1':
      return {
        type: 'market',
        identifier: item.market.marketId
      };
    case 'VaultV1':
      return {
        type: 'vault',
        identifier: item.vault.address
      };
    default:
      return undefined;
  }
}
const ANALYTICS_EXPOSURE_TAB: Record<ExposureTabKey, ExposureTab> = {
  VaultsAndMarkets: 'vaults_and_markets',
  Collateral: 'collateral',
  Adapters: 'adapters',
  PublicAllocator: 'public_allocator'
};
export interface VaultV2ExposureClientProps {
  address: Address;
  chainId: ChainId;
  data: VaultV2ExposureSectionData;
}
const VaultV2ExposureClient = ({
  address,
  chainId,
  data
}: VaultV2ExposureClientProps) => {
  // 'use no memo': `vaultV2ExposureTable` keeps one reference while its rows change, so a
  // compiled component would cache the rows of an earlier filter.
  'use no memo';

  const [tab, setTab] = useState<ExposureTabKey>('VaultsAndMarkets');
  const dataTab: VaultV2ExposureTab = tab === 'PublicAllocator' ? 'VaultsAndMarkets' : tab;
  const {
    rows: vaultV2ExposureData,
    hasMarketAllocation,
    hasOnlyMarketV1Adapters
  } = useVaultV2ExposureData({
    data,
    tab: dataTab
  });

  // The Vault V1 note only applies when the vault actually has a Vault V1 adapter.
  const hasVaultV1Adapter = data.exposure.adapters.some(adapter => adapter.vaultV1 !== null);
  const vaultSupplyToken = useMemo(() => getParsedValue(data.asset, parseApiAsset), [data.asset]);
  const columns = getColumnsVaultV2Exposure({
    tab: dataTab,
    vaultSupplyToken,
    vaultAddress: address,
    chainId,
    hasOnlyMarketV1Adapters
  });
  const columnLabels = getVaultV2ExposureColumnLabels({
    tab: dataTab,
    vaultSupplyToken
  });
  const vaultV2ExposureTable = useTable({
    features: vaultV2ExposureTableFeatures,
    data: vaultV2ExposureData,
    columns
  });
  return <div className="gap-sm flex h-full w-full scroll-mt-[calc(var(--sticky-offset,0px)+16px)] flex-col" id="exposure-section">
      <div className="gap-sm flex flex-wrap items-center justify-between">
        <SubSectionTitle>Exposure</SubSectionTitle>
        {hasMarketAllocation && <div className="gap-sm flex flex-wrap items-center">
            {EXPOSURE_TABS.map(({
          key,
          label
        }) => <Button key={key} variant={tab === key ? 'secondary' : 'ghost'} size="sm" onClick={() => {
          if (key !== tab) {
            trackExposureTabChanged({
              tab: ANALYTICS_EXPOSURE_TAB[key],
              vaultAddress: address,
              chainId
            });
          }
          setTab(key);
        }}
        // Instant pill toggle (no transition, like the pre-@repo/ui Button): `transition-all`
        // would animate the ghost->secondary border from currentColor (white in dark) on
        // click. Dark active pill stays flat.
        className={cn('transition-none', tab === key && 'dark:border-transparent dark:bg-secondary dark:shadow-none')}>
                {label}
              </Button>)}
          </div>}
      </div>
      {tab === 'PublicAllocator' ? <PublicAllocatorSection address={address} chainId={chainId} data={data} /> : <TableScrollArea className="grid h-full w-full rounded-[20px]">
          {vaultV2ExposureTable.getRowModel().rows.length > 0 && <Table.Table tableVariant="compact" className="[&_.td-content]:pr-[16px] [&_th]:pr-[16px]">
              <Table.Thead>
                {vaultV2ExposureTable.getHeaderGroups().map(headerGroup => <TrHeaderGroup key={headerGroup.id} columnLabels={columnLabels} headerGroup={headerGroup} />)}
              </Table.Thead>
              <Table.Tbody>
                {vaultV2ExposureTable.getRowModel().rows.map(row => {
            const rowData = row.original;
            if (rowData.type === 'category') {
              return <VaultV2ExposureCategoryRow key={`category-${rowData.label}`} label={rowData.label} columnCount={vaultV2ExposureTable.getAllColumns().length} />;
            }
            const destination = getExposureRowDestination(rowData.item);
            const onNavigate = rowData.href && destination ? () => trackExposureRowClicked({
              sourceVaultAddress: address,
              sourceVaultVersion: 'v2',
              sourceChainId: chainId,
              destinationType: destination.type,
              destinationIdentifier: destination.identifier,
              destinationChainId: chainId,
              tab: 'vaults_and_markets'
            }) : undefined;
            return <TrBodyRow key={row.id} row={row} href={rowData.href} onNavigate={onNavigate} />;
          })}
              </Table.Tbody>
            </Table.Table>}
          {vaultV2ExposureTable.getRowModel().rows.length === 0 && <TableNoResults text={tab === 'Collateral' && hasVaultV1Adapter ? 'This Vault V2 does not have direct exposure to Morpho market adapters. Please note that it remains exposed via the underlying markets and collateral of the Vault V1 adapter.' : 'No exposure found.'} height="250px" style={{
        borderRadius: '0'
      }} />}
        </TableScrollArea>}
    </div>;
};
export default VaultV2ExposureClient;